import dayjs, {Dayjs} from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import {get} from "lodash";
import {ContactDetailsType, ContactDetailsUpdateData, EventDetails, IGetPhoneNumber} from "../../../declarations";
import {CommunicateBy, CommunicationDirection, EventType, PaymentPlanFrequency, PaymentPlanType} from "../../../enums";
import {DATE_FORMAT_V2, InputUtils} from "../../../helpers";
import {
  ChipType,
  Colors,
  IconAccountUpdate,
  IconApp,
  IconCall,
  IconCallIn,
  IconCallOut,
  IconChatSquare,
  IconCommentOutlined,
  IconCreditReport,
  IconDisputeOutlined,
  IconDSC,
  IconEmail,
  IconEmailIn,
  IconEmailOut,
  IconInventoryTimeline,
  IconLegalCollectionsSquared,
  IconLetter,
  IconLetterSquare,
  IconPartnerAction,
  IconPayment,
  IconReminder,
  IconSmsIn,
  IconSmsOut,
  IconTriggerIn,
  planTypeToLabelMap,
} from "../../../ui";

dayjs.extend(utc);
dayjs.extend(timezone);

const EventTypeMap = new Map<EventType, EventDetails>([
  ["PHONE_CALL_INCOMING", {labelKey: "Phone Call Incoming", icon: IconCallIn}],
  ["PHONE_CALL_MISSED_INCOMING", {labelKey: "Missed Incoming Call", icon: IconCallIn}],
  ["PHONE_CALL_DIALED", {labelKey: "Outbound Call", icon: IconCallOut}],
  ["SCHEDULED_PHONE_CALL", {labelKey: "Scheduled Phone Call", icon: IconCallOut}],
  ["SMS_RECEIVED", {labelKey: "SMS Received", icon: IconSmsIn}],
  ["SMS_DELIVERED", {labelKey: "SMS Sent", icon: IconSmsOut}],
  ["SMS_UNDELIVERED", {labelKey: "SMS Undelivered", icon: IconSmsOut}],
  ["SMS_LINK_CLICK", {labelKey: "SMS Link Click", icon: IconSmsIn}],
  ["SMS_PAGE_CLICK", {labelKey: "SMS Page Click", icon: IconSmsIn}],
  ["SMS_OPT_OUT", {labelKey: "SMS Opt Out", icon: IconSmsIn}],
  ["SCHEDULED_SMS", {labelKey: "Scheduled SMS", icon: IconSmsOut}],
  ["EMAIL_RECEIVED", {labelKey: "Email Received", icon: IconEmailIn}],
  ["EMAIL_DELIVERED", {labelKey: "Email Sent", icon: IconEmailOut}],
  ["EMAIL_OPEN", {labelKey: "Email Open", icon: IconEmailIn}],
  ["EMAIL_CLICK", {labelKey: "Email Click", icon: IconEmailIn}],
  ["EMAIL_UNSUBSCRIBE", {labelKey: "Email Unsubscribe", icon: IconEmailIn}],
  ["EMAIL_ATTEMPTED", {labelKey: "Email/SMS Failed", icon: IconEmailOut}],
  ["EMAIL_SUPPRESSED", {labelKey: "Email/SMS Failed", icon: IconEmailOut}],
  ["SCHEDULED_EMAIL", {labelKey: "Scheduled Email", icon: IconEmailOut}],
  ["LETTER_SENT", {labelKey: "Letter Sent", icon: IconLetterSquare}],
  ["LETTER_RETURNED", {labelKey: "Letter Returned", icon: IconLetterSquare}],
  ["LETTER_PROBABLY_DELIVERED", {labelKey: "Letter Probably Delivered", icon: IconLetterSquare}],
  ["WEBCHAT_CHAT", {labelKey: "Chat", icon: IconChatSquare}],
  ["WEBCHAT_OFFLINE", {labelKey: "Offline Chat", icon: IconChatSquare}],
  ["PAYMENT_PLAN_SELECTED", {labelKey: "Payment Plan Selected", icon: IconPayment}],
  ["PAYMENT_PLAN_RESCHEDULED", {labelKey: "Payment Plan Rescheduled", icon: IconPayment}],
  ["PAYMENT_PLAN_DEACTIVATED", {labelKey: "Payment Plan Deactivated", icon: IconPayment}],
  ["PAYMENT_RECEIVED", {labelKey: "Payment Received", icon: IconPayment}],
  ["PAYMENT_FAILED", {labelKey: "Payment Failed", icon: IconPayment}],
  ["PAYMENT_CHARGEBACK", {labelKey: "Payment Chargeback", icon: IconPayment}],
  ["PAYMENT_REFUND", {labelKey: "Payment Refund", icon: IconPayment}],
  ["PAYMENT_VOID", {labelKey: "Payment Void", icon: IconPayment}],
  ["PAYMENT_RETURNED", {labelKey: "Payment Returned", icon: IconPayment}],
  ["PAYMENT_REFUND_IN_PROGRESS", {labelKey: "Refund In Progress", icon: IconPayment}],
  ["PAYMENT_REFUND_FAILED", {labelKey: "Refund Failed", icon: IconPayment}],
  ["PAYMENT_SUBMITTED", {labelKey: "Payment Submitted", icon: IconPayment}],
  ["PAYMENT_FAILED_RETRY", {labelKey: "Payment Failed (Retry)", icon: IconPayment}],
  ["PAYMENT_PENDING", {labelKey: "Payment Pending", icon: IconPayment}],
  ["ACCOUNT_UPDATE_ADD", {labelKey: "Data Change Log (Added)", icon: IconInventoryTimeline}],
  ["ACCOUNT_UPDATE_DELETE", {labelKey: "Data Change Log (Removed)", icon: IconInventoryTimeline}],
  ["ACCOUNT_UPDATE", {labelKey: "Data Change Log (Updated)", icon: IconInventoryTimeline}],
  ["CONTACT_US", {labelKey: "Contact Us", icon: IconEmailIn}],
  ["DISPUTE_FILED", {labelKey: "Dispute filed", icon: IconDisputeOutlined}],
  ["VERIFICATION_REQUEST_FILED", {labelKey: "Verification request filed", icon: IconDisputeOutlined}],
  ["USER_POSTPONED_CREDIT_REPORT", {labelKey: "App visit", icon: IconApp}],
  ["USER_VISIT_PAYMENT_PLAN_SUMMARY", {labelKey: "App visit", icon: IconApp}],
  ["USER_VISIT_PAYMENT_PLANS", {labelKey: "App visit", icon: IconApp}],
  ["USER_SUBMITTED_QUESTIONNAIRE", {labelKey: "Questionnaire submitted", icon: IconApp}],
  ["LOG_IN", {labelKey: "App visit", icon: IconApp}],
  ["VALIDATION_IN_APP", {labelKey: "App visit", icon: IconApp}],
  ["COLLECTION_NOTICE_TRIGGER", {labelKey: "External Data Received", icon: IconTriggerIn}],
  ["UNKNOWN", {labelKey: "Unknown", icon: IconAccountUpdate}],
  ["INSTALLMENT_DUE_DATE_UPDATED", {labelKey: "Payment Rescheduled", icon: IconPayment}],
  ["INSTALLMENT_SKIPPED", {labelKey: "Installment skipped", icon: IconPayment}],
  ["COMMENT", {labelKey: "Comment", icon: IconCommentOutlined}],
  ["CREDIT_REPORT", {labelKey: "Credit Report", icon: IconCreditReport}],
  ["REMINDER_SHOWED", {labelKey: "Reminder", icon: IconReminder}],
  ["REMINDER_QUEUED", {labelKey: "Reminder", icon: IconReminder}],
  ["CREDIT", {labelKey: "Credit", icon: IconPayment}],
  ["CREDIT_REVERSED", {labelKey: "Credit Reversed", icon: IconPayment}],
  ["DEBIT", {labelKey: "Debit", icon: IconPayment}],
  ["CHANNEL_PREFERENCES_CHANGE", {labelKey: "Update: Channel Preferences", icon: IconAccountUpdate}],
  ["CONTACT_DETAILS_UPDATE", {labelKey: "Update: Contact Details", icon: IconAccountUpdate}],
  ["FLAG_UPDATE", {labelKey: "Update: Flags", icon: IconAccountUpdate}],
  ["CREDITOR_RECALL_UPDATE", {labelKey: "Update: Creditor Recall edited", icon: IconAccountUpdate}],
  ["STATUTE_OF_LIMITATION_UPDATE", {labelKey: "Update: Statute of Limitations ", icon: IconAccountUpdate}],
  ["CONSUMER_DETAILS_UPDATE", {labelKey: "Update: Consumer Details ", icon: IconAccountUpdate}],
  ["REPRESENTATION_UPDATE", {labelKey: "Update: Representation Status ", icon: IconAccountUpdate}],
  ["DOCUMENT_REQUEST_CREATED", {labelKey: "Document request created", icon: IconAccountUpdate}],
  ["DOCUMENT_REQUEST_COMPLETED", {labelKey: "Document request completed", icon: IconAccountUpdate}],
  ["PARTNER_PAYMENT_SUBMITTED", {labelKey: "Partner action", icon: IconPartnerAction}],
  ["PARTNER_CREDITOR_RECALLED", {labelKey: "Partner action", icon: IconPartnerAction}],
  ["CREDITOR_ADJUSTMENT", {labelKey: "Partner action", icon: IconPartnerAction}],
  ["LEGAL_PRE_LEGAL", {labelKey: "Legal Collection", icon: IconLegalCollectionsSquared}],
  ["LEGAL_PLACEMENT_TO_VENDOR", {labelKey: "Legal Collection", icon: IconLegalCollectionsSquared}],
  ["LEGAL_PLACEMENT", {labelKey: "Legal Collection", icon: IconLegalCollectionsSquared}],
  ["LEGAL_JUDGMENT", {labelKey: "Legal Collection", icon: IconLegalCollectionsSquared}],
  ["LEGAL_REVERTED", {labelKey: "Legal Collection", icon: IconLegalCollectionsSquared}],
  ["LEGAL_CLOSED", {labelKey: "Legal Collection", icon: IconLegalCollectionsSquared}],
  ["LEGAL_SUIT", {labelKey: "Legal Collection", icon: IconLegalCollectionsSquared}],
  ["AFFIDAVIT_GENERATED_AND_REVIEWED", {labelKey: "Legal Collection", icon: IconLegalCollectionsSquared}],
  ["NY_AFFIRMATION_OF_SALE_GENERATED_AND_REVIEWED", {labelKey: "Legal Collection", icon: IconLegalCollectionsSquared}],
  ["VOICE_AI_CALL", {labelKey: "Voice AI Call", icon: IconCallOut}],
  ["WHATSAPP_SENT", {labelKey: "WhatsApp Sent", icon: IconSmsOut}],
  ["WHATSAPP_DELIVERED", {labelKey: "WhatsApp Delivered", icon: IconSmsOut}],
  ["WHATSAPP_READ", {labelKey: "WhatsApp Read", icon: IconSmsOut}],
  ["WHATSAPP_FAILED", {labelKey: "WhatsApp Failed", icon: IconSmsOut}],
  ["WHATSAPP_RECEIVED", {labelKey: "WhatsApp Received", icon: IconSmsIn}],
  ["WHATSAPP_LINK_CLICK", {labelKey: "WhatsApp Link Click", icon: IconSmsIn}],
  ["WHATSAPP_PAGE_CLICK", {labelKey: "WhatsApp Page Click", icon: IconSmsIn}],
  ["WHATSAPP_OPT_OUT", {labelKey: "WhatsApp Opt Out", icon: IconSmsIn}],
  ["DSC_OFFER_GENERATED", {labelKey: "DSC Offer Generated", icon: IconDSC}],
]);

const getNameFromEmail = (input: string) => {
  const parts = input.split("@");
  return parts[0];
};

const getPrincipalLabel = (principal: string) => {
  if (principal) {
    if (principal.includes("@")) return principal;
    if (InputUtils.containsOnlyDigits(principal)) return CommunicateBy.USER;
    return principal;
  }
  return CommunicateBy.SYSTEM;
};

const getSuffixStatusLabel = (prefix: string, status: string) => {
  if (status && status.startsWith(prefix)) {
    return InputUtils.prettierSnakeCase(status.slice(prefix.length));
  }
  return InputUtils.prettierSnakeCase(status);
};

const CallContactColorsMap = (): Map<string, {color: string}> => {
  return new Map<string, {color: string}>([
    ["right_party_contact", {color: Colors.green._700}],
    ["third_party_contact", {color: Colors.green._700}],
    ["voicemail", {color: Colors.orange._600}],
    ["no_answer", {color: Colors.red._700}],
    ["unverified_contact", {color: Colors.red._700}],
    ["disconnected_number", {color: Colors.red._700}],
    ["wrong_number", {color: Colors.red._700}],
    ["lost_connection", {color: Colors.red._700}],
  ]);
};

const ChatContactColorsMap = (): Map<string, {color: string}> => {
  return new Map<string, {color: string}>([
    ["concluded", {color: Colors.green._700}],
    ["abandoned", {color: Colors.orange._600}],
    ["missed", {color: Colors.red._700}],
  ]);
};

const getDateLabel = (date: Dayjs): string => {
  const today = dayjs().tz("America/New_York");
  if (date.isAfter(today, "day")) return "";
  if (date.isSame(today, "day")) return "Today";
  if (date.isSame(today.subtract(1, "day"), "day")) return "Yesterday";
  if (date.isAfter(today.subtract(5, "day")) && date.isBefore(today.subtract(1, "day"))) {
    return `Last ${date.format("dddd")}`;
  }
  return date.format(DATE_FORMAT_V2);
};

const getPhoneNumber = ({direction, to_number, from_number}: IGetPhoneNumber) => {
  if (direction === CommunicationDirection.OUTBOUND) {
    if (to_number) {
      return `To: ${to_number}`;
    }
  }
  if (from_number) {
    return `From: ${from_number}`;
  }
};
const getPaymentPlanSubtitleByType = ({
  paymentPlanType,
  frequency,
}: {
  paymentPlanType: PaymentPlanType;
  frequency: PaymentPlanFrequency;
}) => {
  if (paymentPlanType === "INTERIM") {
    return "Temp relief program";
  }
  if (["HARDSHIP", "MATRIX_HARDSHIP", "MATRIX_HARDSHIP_BUNDLE", "HARDSHIP_EXTENSION"].includes(paymentPlanType)) {
    return "Hardship";
  }
  if (["DOWN_PAYMENT", "MATRIX_DOWN_PAYMENT", "MATRIX_DOWN_PAYMENT_BUNDLE"].includes(paymentPlanType)) {
    return "Down Payment";
  }
  if (paymentPlanType === "TAX_SEASON") {
    return "Tax season";
  }
  if (paymentPlanType === "PAYOFF") {
    return "Early payoff";
  }
  if (paymentPlanType === "BUNDLE") {
    return "Bundle";
  }
  return get(planTypeToLabelMap.get(frequency), "labelKey", "");
};

const actionToTextMap: Record<string, string> = {
  delete: "removed",
  add: "added",
  update: "changed to",
};

const getChipInfo = (
  activity: ContactDetailsUpdateData
): {label: string; variant: "warning" | "error" | "success" | "secondary"} | null => {
  if (activity.blocked !== undefined) {
    return {
      label: activity.blocked ? "Blocked" : "Unblocked",
      variant: activity.blocked ? "warning" : "error",
    };
  }
  if (activity.sms_unsubscribe !== undefined) {
    return {
      label: activity.sms_unsubscribe ? "SMS Opt-out" : "SMS Opt-in",
      variant: activity.sms_unsubscribe ? "warning" : "success",
    };
  }
  if (activity.unsubscribe !== undefined) {
    return {
      label: activity.unsubscribe ? "Unsubscribed" : "Resubscribed",
      variant: activity.unsubscribe ? "warning" : "success",
    };
  }
  if (activity.is_primary) {
    return {label: "Primary", variant: "secondary"};
  }
  return null;
};

const getContactIconAndLabel = (type: ContactDetailsType) => {
  switch (type) {
    case "ADDRESS":
      return {icon: IconLetter, label: "Address"};
    case "EMAIL":
      return {icon: IconEmail, label: "Email"};
    default:
      return {icon: IconCall, label: "Phone"};
  }
};

const accountFlagsTypes: FlagType[] = [
  {value: "active_complaint", label: "Active Complaint", variant: "warning"},
  {value: "lawsuit", label: "Lawsuit", variant: "error"},
  {value: "creditor_recalled", label: "Creditor Recalled", variant: "error"},
  {value: "closed", label: "Closed", variant: "error"},
  {value: "fraud_reported", label: "Fraud Reported", variant: "error"},
  {value: "cease_and_desist", label: "Cease And Desist", variant: "error"},
  {value: "freeze_communication", label: "Freeze Communication", variant: "error"},
];

const userFlagsTypes: FlagType[] = [
  {value: "hearing_disability", label: "Hearing Disability", variant: "warning"},
  {value: "reading_disability", label: "Reading Disability", variant: "warning"},
  {value: "active_military", label: "Active Military", variant: "warning"},
  {value: "deceased", label: "Deceased", variant: "error"},
  {value: "incarcerated", label: "Incarcerated", variant: "error"},
  {value: "unhandled_communication", label: "Unhandled Communication", variant: "error"},
];

const unEditableAccountFlagsTypes: FlagType[] = [
  {value: "represented", label: "Represented", variant: "warning"},
  {value: "statute_barred", label: "Statute Barred", variant: "warning"},
];

const accountStatusTypes: FlagType[] = [
  {value: "complaint", label: "Active Complaint", variant: "warning"},
  {value: "creditor_recalled", label: "Creditor Recalled", variant: "error"},
  {value: "closed", label: "Closed", variant: "error"},
  {value: "fraud_reported", label: "Fraud Reported", variant: "error"},
  {value: "open", label: "Open", variant: "inherit"},
  {value: "paid", label: "Paid", variant: "success"},
  {value: "active_dispute", label: "Active Dispute", variant: "warning"},
  {value: "bankruptcy", label: "Bankruptcy", variant: "error"},
  {value: "legal_collection", label: "Legal Collections", variant: "error"},
  {value: "external_collector", label: "External Collector", variant: "info"},
  {value: "deceased", label: "Deceased", variant: "error"},
  {value: "coerced_debt", label: "Coerced debt", variant: "error"},
  {value: "returned", label: "Returned", variant: "error"},
  {value: "cured", label: "Cured", variant: "success"},
  {value: "graduated", label: "Graduated", variant: "info"},
];

type FlagType = {value: string; label: string; variant: ChipType["variant_"]};

const allFlagTypes = Object.fromEntries(
  [...accountFlagsTypes, ...userFlagsTypes, ...unEditableAccountFlagsTypes].map((f) => [f.value, f])
);

export {
  getPaymentPlanSubtitleByType,
  getPhoneNumber,
  getDateLabel,
  CallContactColorsMap,
  ChatContactColorsMap,
  getSuffixStatusLabel,
  getPrincipalLabel,
  getNameFromEmail,
  EventTypeMap,
  actionToTextMap,
  getChipInfo,
  getContactIconAndLabel,
  allFlagTypes,
  accountStatusTypes,
  accountFlagsTypes,
  userFlagsTypes,
};
export type {FlagType};
